/* Subscription UX layer — mounts globally inside AppShell.
 *
 * Responsibilities (iter73 — Discovery Credits era):
 *   - Poll GET /api/subscription/status once per session.
 *   - If nickname_needs_pick → show NicknameOnboarding modal (blocking).
 *   - If credit_reminder ∈ {3,2,1,0} and !dismissed → show ReminderBanner.
 *   - Legacy `cap_reached` still hides Level20Celebration since level
 *     cap is retired (level = achievement now).
 *
 * When a user's credits hit zero, the /discover endpoint returns 402 —
 * the actual "Upgrade to keep exploring" modal fires from the discover
 * page itself, so we don't second-guess navigation timing here.
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Loader2, Camera, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';
import { CREDITS_CHANGED_EVENT } from './CreditsIndicator';
import ForcePasswordChange from './ForcePasswordChange';

const CREDIT_REMINDER_COPY = {
  3: "3 Discovery Credits left — save them for the shots that matter.",
  2: "2 Discovery Credits remaining in your Trailhead adventure.",
  1: "Only 1 Discovery Credit left. Upgrade to keep identifying wildlife.",
  0: "You've used your Discovery Credits. Upgrade to keep identifying wildlife.",
};

// Auto-generated nickname pattern — Demo_4694, TestUser_5431, Ana_1802,
// Marcus_9081, Bug_8515… anything shaped like <letters>_<3+digits>.
// Real-picked nicknames rarely land exactly on this shape, so it's a
// reliable "we still generated this for you" signal for legacy users.
const AUTO_NICKNAME_RE = /^[A-Za-z]+_\d{3,}$/;
const NICK_SOFT_ACK_KEY = 'wq_nickname_soft_ack';

export default function SubscriptionLayer() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [showNick, setShowNick] = useState(false);
  const [showNickSoft, setShowNickSoft] = useState(false);
  const [showPicture, setShowPicture] = useState(false);
  const [dismissedReminderThisSession, setDismissedReminderThisSession] = useState(false);
  const [dismissedPictureBanner, setDismissedPictureBanner] = useState(() => {
    try { return localStorage.getItem('wq_picture_banner_dismissed') === '1'; }
    catch (_e) { return false; }
  });

  const refresh = () => {
    return api.get('/subscription/status')
      .then(r => {
        setStatus(r.data);
        const isAdminTier = r.data.tier === 'admin';
        if (r.data.nickname_needs_pick && !isAdminTier) {
          setShowNick(true);
        } else if (r.data.profile_picture_needs_pick && !isAdminTier) {
          setShowPicture(true);
        } else if (!isAdminTier && AUTO_NICKNAME_RE.test(r.data.nickname || '')) {
          // Legacy user still on a seed-generated handle — offer a rename
          // once per browser (soft, dismissible).
          let acked = false;
          try { acked = localStorage.getItem(NICK_SOFT_ACK_KEY) === '1'; } catch (_e) {}
          if (!acked) setShowNickSoft(true);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    let dead = false;
    refresh().then(() => { if (dead) return; });
    // Re-fetch whenever any page announces a credits change (post-discover).
    const on = () => { if (!dead) refresh(); };
    window.addEventListener(CREDITS_CHANGED_EVENT, on);
    return () => { dead = true; window.removeEventListener(CREDITS_CHANGED_EVENT, on); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || !status) return null;

  // Highest-priority gate — an admin reset left this account on a temp
  // credential. Block all other UI (including nickname / picture) until
  // the user picks a fresh password so iCloud Keychain can't autofill
  // the stale one on their next device.
  if (status.must_change_password) {
    return <ForcePasswordChange onCompleted={() => setStatus(s => ({ ...s, must_change_password: false }))} />;
  }

  const isAdminTier = status.tier === 'admin';
  // credit_reminder is 3/2/1/0/null. Show the banner only if this
  // session hasn't dismissed it yet.
  const cr = status.credit_reminder;
  const showReminder = cr !== null && cr !== undefined
    && !dismissedReminderThisSession
    && !showNick && !showPicture;
  const showPictureBanner = !isAdminTier &&
    !status.profile_picture_needs_pick &&
    !status.has_custom_picture &&
    !dismissedPictureBanner &&
    !showNick && !showPicture;

  return (
    <>
      <AnimatePresence>
        {showReminder && (
          <ReminderBanner
            threshold={cr}
            message={CREDIT_REMINDER_COPY[cr] || `${cr} Discovery Credits remaining.`}
            onUpgrade={() => nav('/membership')}
            onDismiss={async () => {
              setDismissedReminderThisSession(true);
              try { await api.post('/subscription/dismiss-reminder', { level: cr }); } catch { /* non-fatal */ }
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showPictureBanner && (
          <PictureUploadBanner
            onOpen={() => setShowPicture(true)}
            onDismiss={() => {
              setDismissedPictureBanner(true);
              try { localStorage.setItem('wq_picture_banner_dismissed', '1'); } catch (_e) {}
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showNick && (
          <NicknameOnboarding
            currentValue={status.nickname}
            onClaimed={(nick) => {
              setStatus(s => ({ ...s, nickname: nick, nickname_needs_pick: false }));
              setShowNick(false);
              if (status.profile_picture_needs_pick) setShowPicture(true);
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showNickSoft && !showNick && (
          <NicknameOnboarding
            currentValue={status.nickname}
            soft
            onClaimed={(nick) => {
              setStatus(s => ({ ...s, nickname: nick }));
              setShowNickSoft(false);
              try { localStorage.setItem(NICK_SOFT_ACK_KEY, '1'); } catch (_e) {}
            }}
            onDismiss={() => {
              setShowNickSoft(false);
              try { localStorage.setItem(NICK_SOFT_ACK_KEY, '1'); } catch (_e) {}
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showPicture && (
          <PictureOnboarding
            currentPicture={status.picture || user.picture}
            required={!!status.profile_picture_needs_pick}
            onUploaded={async (pictureUrl) => {
              setStatus(s => ({
                ...s,
                picture: pictureUrl,
                has_custom_picture: true,
                profile_picture_needs_pick: false,
              }));
              setShowPicture(false);
              try { await checkAuth(); } catch (_e) {}
            }}
            onDismiss={() => setShowPicture(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}


/* ============================================================ */
/*  Nickname onboarding — real-time check + auto-suggestions     */
/*                                                               */
/*  Two entry paths:                                             */
/*    - hard (default): fires when nickname_needs_pick is true.  */
/*      No dismiss button; the modal is blocking.                */
/*    - soft (`soft` prop): fires once for legacy users who      */
/*      still have an auto-generated handle like Ana_1802 /      */
/*      Demo_4694. Users can skip via "Not now".                 */
/* ============================================================ */
function NicknameOnboarding({ currentValue, onClaimed, soft = false, onDismiss }) {
  const [value, setValue] = useState('');
  const [status, setStatus] = useState({ available: null, valid: null, reason: null, suggestions: [] });
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!value || value.length < 3) {
      setStatus({ available: null, valid: null, reason: null, suggestions: [] });
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/nicknames/check?value=${encodeURIComponent(value)}`);
        setStatus(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const canClaim = value.length >= 3 && status.available === true && status.valid !== false;

  const claim = async (nick) => {
    setClaiming(true);
    try {
      const { data } = await api.post('/nicknames/claim', { value: nick });
      toast.success(`Welcome, ${data.nickname}!`);
      onClaimed(data.nickname);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim nickname');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={soft ? onDismiss : undefined}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid={soft ? 'nickname-soft-modal' : 'nickname-modal'}>
      <motion.div
        onClick={e => e.stopPropagation()}
        initial={{ scale: 0.9, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-3xl bg-[#0f1a15] border border-white/10 p-6 space-y-4">
        <div className="text-center space-y-2">
          <Sparkles className="w-8 h-8 text-accent mx-auto" />
          <h2 className="font-serif text-2xl">
            {soft ? 'Make your Explorer name yours' : 'Choose your Explorer Nickname'}
          </h2>
          {soft ? (
            <p className="text-xs text-muted-foreground leading-relaxed">
              We noticed you're still using an auto-generated handle
              (<span className="font-mono text-white/70">{currentValue}</span>).
              Pick something you'd be proud to see on the community feed — this is how other Explorers will know you.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              This is how other Explorers will see you throughout WildQuest. Your email stays private.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="relative">
            <input
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              maxLength={20}
              autoFocus
              placeholder={currentValue || 'e.g. ForestWalker'}
              data-testid="nickname-input"
              className="w-full h-11 rounded-xl bg-black/50 border border-white/15 px-4 pr-10 text-sm outline-none focus:border-accent/60"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> :
               status.available === true ? <span className="text-[10px] text-emerald-400 uppercase tracking-widest">Available</span> :
               status.available === false ? <span className="text-[10px] text-red-400 uppercase tracking-widest">Taken</span> :
               null}
            </span>
          </div>
          {status.reason && (
            <p className="text-xs text-red-300" data-testid="nickname-reason">{status.reason}</p>
          )}
          {status.suggestions?.length > 0 && (
            <div className="pt-1" data-testid="nickname-suggestions">
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Try one of these:</p>
              <div className="flex flex-wrap gap-1.5">
                {status.suggestions.map((s, i) => (
                  <button key={i} type="button" onClick={() => setValue(s)}
                    data-testid="nickname-suggestion"
                    className="text-xs px-2.5 py-1 rounded-full bg-accent/10 border border-accent/40 text-accent hover:bg-accent/20">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => claim(value)}
          disabled={!canClaim || claiming}
          data-testid="nickname-claim-btn"
          className="w-full h-11 rounded-full bg-accent text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-40">
          {claiming ? 'Claiming…' : (soft ? 'Save New Nickname' : 'Claim Nickname')}
        </button>
        {soft && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid="nickname-soft-dismiss"
            className="w-full text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white/80"
          >
            Not now — keep {currentValue || 'my current name'}
          </button>
        )}
        <p className="text-[10px] text-muted-foreground text-center">3–20 characters · letters, numbers, underscores.</p>
      </motion.div>
    </motion.div>
  );
}


/* ============================================================ */
/*  Reminder banner — driven by Discovery Credits threshold      */
/*  (3 / 2 / 1 / 0)                                              */
/* ============================================================ */
function ReminderBanner({ threshold, message, onDismiss, onUpgrade }) {
  const zero = threshold === 0;
  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
      className="fixed top-3 inset-x-3 z-50 max-w-md mx-auto"
      data-testid={`credit-reminder-t${threshold}`}>
      <div className="rounded-2xl px-4 py-2.5 flex items-center gap-3 border backdrop-blur-md"
        style={{ background: zero
                   ? 'linear-gradient(135deg, rgba(244,63,94,0.22), rgba(0,0,0,0.6))'
                   : 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(0,0,0,0.55))',
                 borderColor: zero ? 'rgba(244,63,94,0.45)' : 'rgba(251,191,36,0.4)' }}>
        <Sparkles className={`w-4 h-4 flex-shrink-0 ${zero ? 'text-rose-300' : 'text-amber-300'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] uppercase tracking-widest ${zero ? 'text-rose-200' : 'text-amber-200'}`}>
            {zero ? 'Credits exhausted' : `${threshold} credit${threshold === 1 ? '' : 's'} left`}
          </p>
          <p className="text-xs text-white/95 leading-snug">{message}</p>
        </div>
        <button onClick={onUpgrade} data-testid="reminder-upgrade"
          className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 font-semibold">
          Upgrade
        </button>
        <button onClick={onDismiss} data-testid="reminder-dismiss"
          className="p-1 rounded-full hover:bg-white/10">
          <X className="w-3.5 h-3.5 text-white/70" />
        </button>
      </div>
    </motion.div>
  );
}


/* ============================================================ */
/*  Legacy Level20Celebration + PlanCard removed in iter73.      */
/*  The level cap is retired — credits are the economic gate.    */
/* ============================================================ */



/* ============================================================ */
/*  Picture onboarding — required upload after nickname pick     */
/* ============================================================ */
function PictureOnboarding({ currentPicture, required, onUploaded, onDismiss }) {
  const [previewUrl, setPreviewUrl] = useState(currentPicture || null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Picture too large (max 5MB)');
      return;
    }
    if (!/^image\/(jpe?g|png|webp|gif)$/i.test(f.type)) {
      toast.error('Use JPG, PNG, WEBP or GIF');
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
  };

  const submit = async () => {
    if (!file) {
      toast.error('Choose a picture first');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/profile/picture', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Explorer picture set!');
      onUploaded(data.picture);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="picture-modal">
      <motion.div initial={{ scale: 0.9, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-3xl bg-[#0f1a15] border border-white/10 p-6 space-y-4">
        <div className="text-center space-y-2">
          <Camera className="w-8 h-8 text-accent mx-auto" />
          <h2 className="font-serif text-2xl">Your Explorer picture</h2>
          <p className="text-xs text-muted-foreground">
            Upload a portrait so other Explorers know who's on the trail. You can change it later from your Profile.
          </p>
        </div>

        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div
              className="w-28 h-28 rounded-full overflow-hidden border-2 border-white/20 bg-black/40 flex items-center justify-center"
              data-testid="picture-preview"
            >
              {previewUrl ? (
                <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-10 h-10 text-white/30" />
              )}
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={e => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="picture-file-input"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="picture-choose-btn"
            className="text-xs uppercase tracking-widest px-4 py-2 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 flex items-center gap-2"
          >
            <Upload className="w-3.5 h-3.5" /> {file ? 'Choose a different one' : 'Choose a photo'}
          </button>
        </div>

        <button
          onClick={submit}
          disabled={!file || uploading}
          data-testid="picture-save-btn"
          className="w-full h-11 rounded-full bg-accent text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-40 flex items-center justify-center gap-2"
        >
          {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : 'Save & Continue'}
        </button>

        {!required && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid="picture-skip-btn"
            className="w-full text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white/80"
          >
            Not now — I'll add it later
          </button>
        )}
        <p className="text-[10px] text-muted-foreground text-center">JPG · PNG · WEBP · GIF · Max 5MB</p>
      </motion.div>
    </motion.div>
  );
}


/* ============================================================ */
/*  Soft banner nudging legacy users to upload their picture     */
/* ============================================================ */
function PictureUploadBanner({ onOpen, onDismiss }) {
  const t = useT();
  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
      className="fixed top-[70px] inset-x-3 z-30 max-w-md mx-auto pointer-events-none"
      data-testid="picture-upload-banner">
      <div className="rounded-2xl px-4 py-2.5 flex items-center gap-3 border backdrop-blur-md pointer-events-auto"
        style={{ background: 'linear-gradient(135deg, rgba(45,106,79,0.25), rgba(0,0,0,0.6))',
                 borderColor: 'rgba(45,106,79,0.5)' }}>
        <Camera className="w-4 h-4 flex-shrink-0 text-primary-light" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-primary-light">{t('banner.newFeature', 'New feature')}</p>
          <p className="text-xs text-white/95 leading-snug">{t('banner.pictureUploadBody', 'Upload your own Explorer picture — makes the trail feel more like home.')}</p>
        </div>
        <button
          onClick={onOpen}
          data-testid="picture-banner-open"
          className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/10 hover:bg-white/20"
        >
          {t('banner.upload', 'Upload')}
        </button>
        <button
          onClick={onDismiss}
          data-testid="picture-banner-dismiss"
          className="p-1 rounded-full hover:bg-white/10"
        >
          <X className="w-3.5 h-3.5 text-white/70" />
        </button>
      </div>
    </motion.div>
  );
}
